import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import EventComponent from './components/Event';
import AdminModal from './components/AdminModal';
import UserManagementModal from './components/UserManagementModal';
import { preventDevTools } from './utils/security';
import { client, databases, DATABASE_ID, USERS_COLLECTION_ID } from './config/appwrite';

const Header: React.FC = () => {
  const { user, signOut } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const [isUserManagementModalOpen, setIsUserManagementModalOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/login';
  };

  const handleRefreshEvents = () => {
    window.location.reload();
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  if (!user) return null;


  return (
    <>
      <header className="bg-[#0e4653] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">

            {/* Brand */}
            <span className="text-white font-semibold text-base sm:text-lg tracking-tight">
              Burhani Relay Center
            </span>

            {/* Right side */}
            <div className="flex items-center gap-2">

              {/* User avatar + dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsDropdownOpen(p => !p)}
                  className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-xl hover:bg-white/10 transition-colors"
                >
                  {/* Avatar */}
                  <div className="w-8 h-8 rounded-full bg-[#B98027]/80 border-2 border-[#B98027] flex items-center justify-center text-white select-none">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <p className="hidden sm:block text-white text-xs font-semibold">{user.username}</p>
                  <svg
                    className={`w-3.5 h-3.5 text-white/60 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown */}
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 z-50 overflow-hidden">

                    {/* User info header */}
                    <div className="px-4 py-2.5 border-b border-gray-100 mb-1">
                      <p className="text-xs font-semibold text-gray-900">{user.username}</p>
                    </div>

                    {/* Admin actions */}
                    {isAdmin && (
                      <div>
                        <button
                          onClick={() => { setIsAdminModalOpen(true); setIsDropdownOpen(false); }}
                          className="flex items-center gap-2.5 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <svg className="w-4 h-4 text-[#B98027]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Create Event
                        </button>
                        <button
                          onClick={() => { setIsUserManagementModalOpen(true); setIsDropdownOpen(false); }}
                          className="flex items-center gap-2.5 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <svg className="w-4 h-4 text-[#0e4653]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                          Manage Users
                        </button>
                        <div className="border-t border-gray-100 my-1" />
                      </div>
                    )}

                    {/* Logout */}
                    <button
                      onClick={() => { handleSignOut(); setIsDropdownOpen(false); }}
                      className="flex items-center gap-2.5 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Backdrop */}
        {isDropdownOpen && (
          <div className="fixed inset-0 z-40" onClick={() => setIsDropdownOpen(false)} />
        )}
      </header>

      <AdminModal
        isOpen={isAdminModalOpen}
        onClose={() => setIsAdminModalOpen(false)}
        onEventCreated={handleRefreshEvents}
      />
      <UserManagementModal
        isOpen={isUserManagementModalOpen}
        onClose={() => setIsUserManagementModalOpen(false)}
        currentUserRole={user?.role}
        currentUserId={user?.id}
      />
    </>
  );
};

const PrivateRoute: React.FC<{ children: React.ReactNode; requireAdmin?: boolean }> = ({
  children,
  requireAdmin = false,
}) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Check for valid session immediately when component mounts
    const sessionToken = sessionStorage.getItem('sessionToken');
    const storedUser = localStorage.getItem('user');

    // If no session token or no user data, redirect to login
    if (!sessionToken || !storedUser) {
      // Clear all auth data to be safe
      sessionStorage.removeItem('sessionToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      navigate('/login');
      return;
    }

    // Additional check for admin routes
    if (requireAdmin) {
      const userData = JSON.parse(storedUser);
      if (userData.role !== 'admin' && userData.role !== 'superadmin') {
        navigate('/dashboard');
      }
    }
  }, [navigate, requireAdmin]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // If no user or no session token, don't render anything
  if (!user || !sessionStorage.getItem('sessionToken')) {
    return <Navigate to="/login" />;
  }

  if (requireAdmin && (user.role !== 'admin' && user.role !== 'superadmin')) {
    return <Navigate to="/dashboard" />;
  }

  if (user.is_blocked) {
    return <Navigate to="/blocked" />;
  }

  return (
    <>
      <Header />
      {children}
    </>
  );
};

const BlockedPage: React.FC = () => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(false);
  const [message, setMessage] = useState('');

  const checkUnblocked = async () => {
    setIsChecking(true);
    setMessage('');
    try {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) {
        navigate('/login');
        return;
      }
      const userData = JSON.parse(storedUser);
      const doc = await databases.getDocument(DATABASE_ID, USERS_COLLECTION_ID, userData.id);
      if (!doc.is_blocked) {
        const updated = { ...userData, is_blocked: false };
        localStorage.setItem('user', JSON.stringify(updated));
        navigate('/dashboard');
      } else {
        setMessage('Your account is still blocked. Please contact the administrator.');
      }
    } catch {
      setMessage('Unable to check status. Please try again.');
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-[#0e4653] px-8 py-8 text-center">
          <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <h2 className="text-white text-xl font-bold tracking-tight">Account Blocked</h2>
          <p className="text-[#7fb8c8] text-sm mt-1">Your access has been restricted</p>
        </div>
        <div className="px-8 py-7 text-center space-y-5">
          <p className="text-sm text-gray-600 leading-relaxed">
            Your account has been blocked. Please contact the administrator for assistance.
          </p>
          {message && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-left">
              <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xs text-red-700 font-medium">{message}</p>
            </div>
          )}
          <button
            onClick={checkUnblocked}
            disabled={isChecking}
            className="w-full py-3 bg-[#B98027] text-white text-sm font-semibold rounded-xl hover:bg-[#a06f20] focus:outline-none focus:ring-2 focus:ring-[#B98027]/40 disabled:opacity-60 transition-colors"
          >
            {isChecking ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Checking…
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Check Again
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    // Verify Appwrite connection on app startup
    const verifyAppwriteConnection = async () => {
      try {
        await client.ping();
        console.log('✅ Appwrite connection verified successfully');
      } catch (error) {
        console.error('❌ Appwrite connection failed:', error);
      }
    };

    verifyAppwriteConnection();
    preventDevTools();
  }, []);
  
  return (
    <Router>
      <AuthProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <EventComponent />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <PrivateRoute requireAdmin>
                <EventComponent isAdmin />
              </PrivateRoute>
            }
          />
          <Route path="/blocked" element={<BlockedPage />} />
          <Route path="/" element={<Navigate to="/login" />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
};

export default App;
